/**
 * useMatchSocket — WebSocket client for an active match. Connects to
 * /api/match/:ulid/ws, exposes a sendAction helper, and surfaces
 * state-delta + WebRTC signal callbacks for downstream hooks.
 *
 * Auto-reconnects on close (modulo intentional unmount).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { wsUrlFor } from '../../api.js';
import type { PublicTableState, TableMove } from '../types.js';

type Status = 'idle' | 'connecting' | 'open' | 'closed';

export type WelcomeMessage = {
  kind: 'welcome';
  viewer: string;
  publicState: PublicTableState;
};

export type EventDeltaMessage = {
  kind: 'event_delta';
  seq: number;
  events: readonly unknown[];
  publicState: PublicTableState;
};

export type WebRTCSignalMessage = {
  kind: 'webrtc_signal';
  from: string;
  payload: unknown;
};

export type MoveRejectedMessage = {
  kind: 'move_rejected';
  reason: string;
  hint?: string;
};

export type SocketCallbacks = {
  onWelcome?: (msg: WelcomeMessage) => void;
  onEventDelta?: (msg: EventDeltaMessage) => void;
  onWebRTCSignal?: (msg: WebRTCSignalMessage) => void;
  onMoveRejected?: (msg: MoveRejectedMessage) => void;
};

export type MatchSocketHook = {
  status: Status;
  sendMove: (move: TableMove) => void;
  sendWebRTCSignal: (to: string, payload: unknown) => void;
  /** Send a raw message — escape hatch for future kinds. */
  sendRaw: (data: object) => void;
};

export function useMatchSocket(
  matchUlid: string | null,
  callbacks: SocketCallbacks,
): MatchSocketHook {
  const [status, setStatus] = useState<Status>('idle');
  const wsRef = useRef<WebSocket | null>(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (!matchUlid) {
      setStatus('idle');
      return;
    }
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = (): void => {
      if (cancelled) return;
      setStatus('connecting');
      const ws = new WebSocket(wsUrlFor(`/api/match/${matchUlid}/ws`));
      wsRef.current = ws;

      ws.addEventListener('open', () => {
        if (cancelled) {
          ws.close();
          return;
        }
        setStatus('open');
      });

      ws.addEventListener('message', (event) => {
        if (typeof event.data !== 'string') return;
        let parsed: { kind?: string };
        try {
          parsed = JSON.parse(event.data);
        } catch {
          return;
        }
        const cb = callbacksRef.current;
        switch (parsed.kind) {
          case 'welcome':
            cb.onWelcome?.(parsed as unknown as WelcomeMessage);
            break;
          case 'event_delta':
            cb.onEventDelta?.(parsed as unknown as EventDeltaMessage);
            break;
          case 'webrtc_signal':
            cb.onWebRTCSignal?.(parsed as unknown as WebRTCSignalMessage);
            break;
          case 'move_rejected':
            cb.onMoveRejected?.(parsed as unknown as MoveRejectedMessage);
            break;
        }
      });

      ws.addEventListener('close', () => {
        wsRef.current = null;
        if (cancelled) return;
        setStatus('closed');
        // Reconnect with exponential backoff cap at 5s.
        reconnectTimer = setTimeout(connect, 1500);
      });
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [matchUlid]);

  const sendMove = useCallback((move: TableMove) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ kind: 'submit_move', move }));
  }, []);

  const sendWebRTCSignal = useCallback((to: string, payload: unknown) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ kind: 'webrtc_signal', to, payload }));
  }, []);

  const sendRaw = useCallback((data: object) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(data));
  }, []);

  return { status, sendMove, sendWebRTCSignal, sendRaw };
}
